import type {
  Canal,
  Conexion,
  EventoDelCatalogo,
  GuardarBorradorInput,
  NodoEvento,
  Notificacion,
  VariableDeEvento,
  VersionDeFlujo,
} from "../services/notificaciones-service";

// ============================================================================
// Estado del lienzo del diseñador · lógica pura (sin React ni React Flow).
// Las posiciones viven en el `layoutJson` opaco del borrador: { posiciones }.
// ============================================================================

export interface Posicion {
  x: number;
  y: number;
}

export interface EstadoLienzo {
  eventos: NodoEvento[];
  notificaciones: Notificacion[];
  conexiones: Conexion[];
  posiciones: Record<string, Posicion>;
}

export const COLUMNA_EVENTOS_X = 0;
export const COLUMNA_NOTIFICACIONES_X = 440;
export const SEPARACION_Y = 150;

/** Estado inicial del lienzo a partir de una versión (borrador o publicada). */
export function desdeVersion(version: VersionDeFlujo): EstadoLienzo {
  const guardadas = leerPosiciones(version.layoutJson);
  const posiciones: Record<string, Posicion> = {};
  version.eventos.forEach((e, i) => {
    posiciones[e.clave] = guardadas[e.clave] ?? { x: COLUMNA_EVENTOS_X, y: i * SEPARACION_Y };
  });
  version.notificaciones.forEach((n, i) => {
    posiciones[n.clave] = guardadas[n.clave] ?? { x: COLUMNA_NOTIFICACIONES_X, y: i * SEPARACION_Y };
  });
  return {
    eventos: version.eventos,
    notificaciones: version.notificaciones.map(normalizarNotificacion),
    conexiones: version.conexiones,
    posiciones,
  };
}

/** Cuerpo del PUT /borrador (el backend reemplaza el borrador completo). */
export function aBorrador(estado: EstadoLienzo): GuardarBorradorInput {
  const claves = new Set([...estado.eventos.map((e) => e.clave), ...estado.notificaciones.map((n) => n.clave)]);
  const posiciones = Object.fromEntries(
    Object.entries(estado.posiciones)
      .filter(([clave]) => claves.has(clave))
      .map(([clave, p]) => [clave, { x: Math.round(p.x), y: Math.round(p.y) }]),
  );
  return {
    eventos: estado.eventos,
    notificaciones: estado.notificaciones,
    conexiones: estado.conexiones,
    layoutJson: JSON.stringify({ posiciones }),
  };
}

/** ¿Hay cambios sin guardar? (compara el contenido que se enviaría al backend). */
export function hayCambios(actual: EstadoLienzo, guardado: EstadoLienzo): boolean {
  return JSON.stringify(aBorrador(actual)) !== JSON.stringify(aBorrador(guardado));
}

function leerPosiciones(layoutJson: string | null): Record<string, Posicion> {
  if (!layoutJson) return {};
  try {
    const parsed = JSON.parse(layoutJson) as { posiciones?: Record<string, Posicion> };
    return parsed.posiciones ?? {};
  } catch {
    return {};
  }
}

function normalizarNotificacion(n: Notificacion): Notificacion {
  return { ...n, destinatarios: n.destinatarios ?? [], encabezados: n.encabezados ?? [] };
}

/** Clave estable y única para un nodo nuevo (`evento-3`, `email-2`…). */
export function nuevaClave(prefijo: string, existentes: Iterable<string>): string {
  const usadas = new Set(existentes);
  for (let i = 1; ; i++) {
    const clave = `${prefijo}-${i}`;
    if (!usadas.has(clave)) return clave;
  }
}

export function clavesDe(estado: EstadoLienzo): string[] {
  return [
    ...estado.eventos.map((e) => e.clave),
    ...estado.notificaciones.map((n) => n.clave),
    ...estado.conexiones.map((c) => c.clave),
  ];
}

export const ETIQUETA_CANAL: Record<Canal, string> = {
  Email: "Correo electrónico",
  Campana: "Campana del portal",
  WebPush: "Notificación del navegador",
  Sms: "SMS",
  WhatsApp: "WhatsApp",
  Telegram: "Telegram",
  Webhook: "Webhook",
};

/** Notificación nueva con contenido de arranque según el canal. */
export function notificacionNueva(canal: Canal, clave: string): Notificacion {
  const base = { clave, canal, descripcion: null, activa: true, obligatoria: false, encabezados: [] };
  switch (canal) {
    case "Email":
      return {
        ...base,
        nombre: "Correo",
        destinatarios: [{ tipo: "SolicitanteODueno" }],
        asunto: "{{Evento.Nombre}}",
        cuerpoHtml: "<p>Hola {{Destinatario.Nombre}},</p>\n<p>{{Evento.Nombre}}.</p>",
      };
    case "Webhook":
      return {
        ...base,
        nombre: "Webhook",
        destinatarios: [],
        url: "https://",
        cuerpoJson: '{\n  "evento": "{{Evento.Nombre}}",\n  "fecha": "{{Evento.Fecha}}"\n}',
      };
    case "Campana":
      return {
        ...base,
        nombre: "Aviso en campana",
        destinatarios: [{ tipo: "SolicitanteODueno" }],
        titulo: "{{Evento.Nombre}}",
        texto: "{{Evento.Nombre}}",
        enlace: null,
      };
    case "WebPush":
      return {
        ...base,
        nombre: "Notificación del navegador",
        destinatarios: [{ tipo: "SolicitanteODueno" }],
        titulo: "{{Evento.Nombre}}",
        cuerpo: "{{Evento.Nombre}}",
        enlace: null,
      };
    case "Sms":
      return { ...base, nombre: "SMS", destinatarios: [{ tipo: "SolicitanteODueno" }], texto: "Portal de proveedores: {{Evento.Nombre}}" };
    case "WhatsApp":
      return { ...base, nombre: "WhatsApp", destinatarios: [{ tipo: "SolicitanteODueno" }], contentSid: "", variablesWhatsApp: { "1": "{{Evento.Nombre}}" } };
    case "Telegram":
      return { ...base, nombre: "Telegram", destinatarios: [{ tipo: "SolicitanteODueno" }], texto: "{{Evento.Nombre}}", usarMarkdown: false };
  }
}

/** Eventos del catálogo conectados a una notificación. */
export function eventosConectados(
  notificacionClave: string,
  estado: EstadoLienzo,
  catalogo: EventoDelCatalogo[],
): EventoDelCatalogo[] {
  const porCodigo = new Map(catalogo.map((e) => [e.codigo, e]));
  const nodos = new Map(estado.eventos.map((e) => [e.clave, e.tipoEvento]));
  const codigos = new Set(
    estado.conexiones
      .filter((c) => c.notificacionClave === notificacionClave)
      .map((c) => nodos.get(c.eventoClave))
      .filter((c): c is string => !!c),
  );
  return [...codigos].map((c) => porCodigo.get(c)).filter((e): e is EventoDelCatalogo => !!e);
}

/**
 * Variables que la plantilla de una notificación puede usar: las que existen en TODOS
 * los eventos conectados (mismo criterio que la validación del backend al publicar).
 */
export function variablesDisponibles(eventos: EventoDelCatalogo[]): VariableDeEvento[] {
  if (eventos.length === 0) return [];
  const [primero, ...resto] = eventos;
  return primero.variables.filter((v) =>
    resto.every((e) => e.variables.some((o) => o.nombre.toLowerCase() === v.nombre.toLowerCase())),
  );
}

/** Quita un nodo y las conexiones que lo tocan. */
export function quitarNodo(estado: EstadoLienzo, clave: string): EstadoLienzo {
  const { [clave]: _omitida, ...posiciones } = estado.posiciones;
  return {
    eventos: estado.eventos.filter((e) => e.clave !== clave),
    notificaciones: estado.notificaciones.filter((n) => n.clave !== clave),
    conexiones: estado.conexiones.filter((c) => c.eventoClave !== clave && c.notificacionClave !== clave),
    posiciones,
  };
}

/** Conecta evento → notificación (si no existía ya esa conexión). */
export function conectar(estado: EstadoLienzo, eventoClave: string, notificacionClave: string): EstadoLienzo {
  const esEvento = estado.eventos.some((e) => e.clave === eventoClave);
  const esNotificacion = estado.notificaciones.some((n) => n.clave === notificacionClave);
  const repetida = estado.conexiones.some((c) => c.eventoClave === eventoClave && c.notificacionClave === notificacionClave);
  if (!esEvento || !esNotificacion || repetida) return estado;
  const clave = nuevaClave("conexion", clavesDe(estado));
  return {
    ...estado,
    conexiones: [...estado.conexiones, { clave, eventoClave, notificacionClave, activa: true, condicion: null }],
  };
}

// --- Plantillas: autocompletado de variables --------------------------------------

/**
 * Si el cursor está dentro de un `{{` sin cerrar, devuelve dónde empieza la expresión y
 * el texto tecleado (para filtrar sugerencias). null si no hay nada que sugerir.
 */
export function expresionEnCurso(texto: string, cursor: number): { inicio: number; filtro: string } | null {
  const antes = texto.slice(0, cursor);
  const apertura = antes.lastIndexOf("{{");
  if (apertura === -1) return null;
  const tramo = antes.slice(apertura + 2);
  if (tramo.includes("}}") || tramo.includes("\n") || tramo.includes("|")) return null;
  if (!/^[\w.]*$/.test(tramo.trim())) return null;
  return { inicio: apertura, filtro: tramo.trim() };
}

/** Reemplaza la expresión en curso (o inserta en el cursor) por `{{Variable}}`. */
export function insertarVariable(
  texto: string,
  cursor: number,
  variable: string,
): { texto: string; cursor: number } {
  const enCurso = expresionEnCurso(texto, cursor);
  const inicio = enCurso ? enCurso.inicio : cursor;
  const despues = texto.slice(cursor);
  // Si ya venía el cierre tecleado, no duplicarlo.
  const resto = enCurso && despues.startsWith("}}") ? despues.slice(2) : despues;
  const insertado = `{{${variable}}}`;
  return { texto: texto.slice(0, inicio) + insertado + resto, cursor: inicio + insertado.length };
}

export function filtrarVariables(variables: VariableDeEvento[], filtro: string): VariableDeEvento[] {
  const f = filtro.toLowerCase();
  return f ? variables.filter((v) => v.nombre.toLowerCase().includes(f)) : variables;
}
